import {type FC, memo, useCallback} from 'react';
import isEqual from 'react-fast-compare';

import classNames from 'classnames';

import type {GoalApiModel} from '@yourcoach/shared/api/goal';

import {useI18n} from '@yourcoach/i18n';
import MoreIcon from '@yourcoach/shared/assets/icons/more.svg';
import CupIcon from '@yourcoach/shared/assets/icons/primary/Trophy.svg';

import Button from '@src/components/Button';

import styles from './GoalsListItem.module.css';

interface Props<T extends GoalApiModel = GoalApiModel> {
  className?: string;
  goal: T;
  index: number;
  onClick?: (goal: T) => void;
  onMoreButtonClick?: (
    item: T,
    event: React.MouseEvent<Element, MouseEvent>,
  ) => void;
}

const GoalsListItem: FC<Props<GoalApiModel>> = ({
  className,
  goal,
  index,
  onClick,
  onMoreButtonClick,
}) => {
  const {t} = useI18n();
  const onClickCb = useCallback(() => {
    onClick?.(goal);
  }, [onClick, goal]);

  const onMoreButtonClickCb: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      onMoreButtonClick?.(goal, e);
    },
    [goal, onMoreButtonClick],
  );

  return (
    <div
      className={classNames(styles.container, className)}
      onClick={onClickCb}
    >
      <div className={styles.contentContainer}>
        <div className={styles.labelContainer}>
          <CupIcon />
          <p className={styles.label}>
            {t('shared:goal_index', {index: index + 1})}
          </p>
        </div>
        <p className={styles.title}>{goal.title}</p>
        {goal.description ? (
          <p className={styles.description}>{goal.description}</p>
        ) : null}
      </div>
      {onMoreButtonClick ? (
        <Button className={styles.moreButton} onClick={onMoreButtonClickCb}>
          <MoreIcon />
        </Button>
      ) : null}
    </div>
  );
};

export default memo(GoalsListItem, isEqual) as typeof GoalsListItem;
